import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { AppContext } from '../window';
import { useTranslation } from '../i18n';
import { useModal } from '../hooks/useModal';
import { useTracking } from '../hooks/useTracking';
import SignupModal from '../user/SignupModal';
import LoginModal from '../user/LoginModal';
import { safeReturnPath } from '../utils/returnPath';
import { takeSessionLostNotice } from '../api/session';
import { useDisclaimerAccepted } from '../hooks/useDisclaimer';

export interface EntryModals {
  /** A call to action: tracks `cta_click` and opens the signup modal. */
  openSignup: () => void;
  openLogin: () => void;
  /** The signup and login modals; render them once on the page. */
  modals: React.ReactElement;
}

/**
 * The guest entry of a home page (the generic `HomePage` and the custom
 * frontends' pages, `src/sites`): signup and login modals, and the deep links
 * that open them. `?login=1` (an expired email link,
 * CredentialsController::redirectExpiredLink(), a guest on a member route, a
 * lost session) opens the login modal, `?signup=1` (a guest on a profile link)
 * the signup modal; after either AuthRedirect or the signup success goes to
 * `returnUrl`. A fresh guest gets the modal once the age disclaimer is
 * accepted, not on top of it.
 */
export const useEntryModals = (): EntryModals => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(AppContext);
  const signupModal = useModal();
  const loginModal = useModal();
  const track = useTracking();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  // Set when a lost session sent the member here (api/session.ts)
  const [sessionLost, setSessionLost] = useState(false);

  const disclaimerAccepted = useDisclaimerAccepted(user);
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (!disclaimerAccepted || deepLinkHandled.current) {
      return;
    }
    deepLinkHandled.current = true;
    if (!user && query.get('login') === '1') {
      if (takeSessionLostNotice()) {
        setSessionLost(true);
      }
      loginModal.open();
    } else if (!user && query.get('signup') === '1') {
      signupModal.open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disclaimerAccepted]);

  const openSignup = () => {
    track('cta_click');
    signupModal.open();
  };

  const modals = (
    <>
      <SignupModal
        show={signupModal.isOpen}
        onHide={signupModal.close}
        onSignupSuccess={() => navigate(safeReturnPath(query.get('returnUrl')) ?? '/search')}
      />
      <LoginModal
        show={loginModal.isOpen}
        onHide={loginModal.close}
        initialEmail={query.get('email') ?? undefined}
        notice={sessionLost ? t('auth.session_lost') : undefined}
      />
    </>
  );

  return { openSignup, openLogin: loginModal.open, modals };
};
